import { createStyles, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

import { useGetTaskDetailsQuery } from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { Loader } from '../../../shared/Loader';
import { Error as ErrorView } from '../../shared/Error';
import { DetailsTopBar } from '../DetailsTopBar';
import { PlexHeader } from '../plex/PlexHeader';
import { useCloseOnDelete } from '../useCloseOnDelete';
import { PlexTaskActions } from './PlexTaskActions';
import { PlexTaskTitle } from './PlexTaskTitle';
import { TaskAssignee } from './TaskAssignee';
import { TaskBody } from './TaskBody';
import { TaskDueDate } from './TaskDueDate';
import { TaskLabels } from './TaskLabels';
import { TaskRelatedEntities } from './TaskRelatedEntities';
import { TaskStatus } from './TaskStatus';
import { TaskVisibility } from './TaskVisibility';

type Props = {
  taskPermalink: string;
};

export function PlexTaskDetails(props: Props): React.ReactElement {
  const { taskPermalink } = props;
  const classes = useStyles();
  const { data, loading, error } = useGetTaskDetailsQuery({ variables: { taskId: taskPermalink } });

  useCloseOnDelete({ loading, deleted: data?.taskById.deleted || false });

  // TODO(https://github.com/all-hands-hq/source/issues/2050): unread counts
  // useEffect(() => {
  //   if (data?.taskById != null && !data.taskById.notification.isRead) {
  //     markTaskAsSeen({ variables: { taskIds: [data.taskById.id] } });
  //   }
  // }, [data, markTaskAsSeen]);

  // TODO(https://github.com/all-hands-hq/source/issues/2050): analytics
  // useEffect(() => {
  //   if (data?.taskById != null && !data.taskById.currentUserReactions.includes(ReactionType.AnalyticsClick)) {
  //     analyticsTaskClick({ variables: { taskId: data.taskById.id } });
  //   }
  // }, [analyticsTaskClick, data]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView error={error} />;
  }

  const taskData = data?.taskById;

  if (taskData == null) {
    // We are not loading and we did not get an error.
    // This should be illegal state.
    // TODO: redirect to different URL if this occurs
    throw new Error(`Illegal state: ${error} ${loading} ${data}`);
  }

  const plexRelation = taskData.plex.currentUserRelation.state;
  const taskRelation = taskData.currentUserRelation;

  return (
    <div className={classes.root}>
      <DetailsTopBar>
        <PlexHeader
          className={classes.topbar}
          plexName={taskData.plex.name}
          plexColor={taskData.plex.color}
          plexPublishStatus={taskData.publishStatus}
          publishedDate={taskData.createdAt}
          authorName={taskData.author.name}
          authorAvatar={taskData.author.picture}
          authorTitle={taskData.author.jobTitle}
          showUnreadBadge={false}
        />
      </DetailsTopBar>

      <div className={classNames(classes.contentContainer)}>
        <div className={classes.contentData}>
          <PlexTaskTitle
            id={taskData.id}
            title={taskData.title}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />

          <TaskLabels
            id={taskData.id}
            labels={taskData.labels}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />

          <TaskVisibility
            id={taskData.id}
            publishStatus={taskData.publishStatus}
            plexName={taskData.plex.name}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />

          <TaskAssignee
            id={taskData.id}
            assignee={taskData.assignee}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />

          <TaskDueDate
            id={taskData.id}
            dueDate={taskData.dueDate}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />

          <TaskStatus
            id={taskData.id}
            status={taskData.status}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />

          <TaskBody
            id={taskData.id}
            value={taskData.body}
            initialEntities={{ expanded_link: taskData.links, mention: taskData.mentions }}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />

          <TaskRelatedEntities
            id={taskData.id}
            plexId={taskData.plex.id}
            publishStatus={taskData.publishStatus}
            dueDate={taskData.dueDate}
            permalink={taskData.permalink}
            relatedEntities={taskData.relatedEntities}
            plexRelation={plexRelation}
            taskRelation={taskRelation}
          />
        </div>

        <PlexTaskActions
          taskRelation={taskRelation}
          plexRelation={plexRelation}
          taskId={taskData.id}
          relatedEntities={taskData.relatedEntities}
        />
      </div>
    </div>
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    topbar: {
      paddingRight: 0,
    },
    contentContainer: {
      display: 'flex',
      position: 'relative',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    },
    contentData: {
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: spacings.xxlarge,
      paddingRight: spacings.xxlarge * 2,
      paddingTop: spacings.xlarge,
      paddingBottom: spacings.xlarge,
      overflow: 'auto',
      flex: 1,
      width: '100%',
    },
    postContent: {
      marginTop: spacings.small,
    },
  })
);
